import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { BrandCard } from "@/components/public/BrandCard";
import { SingleSelect } from "@/components/public/SingleSelect";
import { MultiSelect } from "@/components/public/MultiSelect";
import { AdSlot } from "@/components/public/AdSlot";
import { SeoHead } from "@/components/public/SeoHead";

export default function BrandsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState("score");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [excludeWarnings, setExcludeWarnings] = useState(false);
  const [q, setQ] = useState("");
  const [certIds, setCertIdsState] = useState(() => (searchParams.get("cert_ids") || "").split(",").filter(Boolean));

  const setCertIds = (next) => {
    setCertIdsState(next);
    const nextParams = new URLSearchParams(searchParams);
    if (next.length) nextParams.set("cert_ids", next.join(","));
    else nextParams.delete("cert_ids");
    setSearchParams(nextParams, { replace: true });
  };

  const { data: brandCerts = [] } = useQuery({
    queryKey: ["public-brand-certifications"],
    queryFn: async () => (await api.get("/public/brand-certifications")).data,
  });

  const { data: brands = [], isLoading } = useQuery({
    queryKey: ["brands", { sort, featuredOnly, q, certIds }],
    queryFn: async () => (await api.get("/public/brands", { params: {
      sort,
      featured_only: featuredOnly,
      q: q || undefined,
      cert_ids: certIds.length ? certIds.join(",") : undefined,
    } })).data,
  });

  const visibleBrands = excludeWarnings
    ? brands.filter((b) => !((b.warnings_total ?? 0) > 0))
    : brands;

  return (
    <div>
      <SeoHead title="Brands — Sustainably Curated" description="Browse sustainable clothing brands with sustainability scores based on manual material reviews." path="/brands" />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8">
        <h1 className="text-[1.75rem] uppercase tracking-[0.25em] text-leaf font-medium mb-3">Brands</h1>
        <p className="text-muted_text">Each brand has a sustainability score based on the items we've manually reviewed. Click a brand for the full breakdown. Filter by the company-level standards a brand holds — see our <Link to="/brand-certifications" className="text-forest underline underline-offset-4 hover:no-underline" data-testid="brands-intro-certifications-link">Brand Certifications</Link> directory for what each one means.</p>
      </section>

      <div className="border-t border-line" />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-[260px_1fr] gap-8">
          {/* Filter sidebar */}
          <aside className="lg:sticky lg:top-24 lg:self-start space-y-5" data-testid="brands-filters">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="w-full px-4 py-2.5 rounded-full border border-line bg-white text-sm focus:outline-none focus:border-forest"
              data-testid="brands-search-input"
            />

            <FilterBlock label="Sort by">
              <SingleSelect
                testId="brands-sort-select"
                value={sort}
                onChange={(v) => setSort(v)}
                options={[
                  { id: "score", label: "Highest score" },
                  { id: "name", label: "Name A\u2013Z" },
                  { id: "newest", label: "Newest" },
                ]}
              />
            </FilterBlock>

            <FilterBlock label="Brand certifications">
              <MultiSelect
                testId="brands-certifications-filter"
                placeholder="Any certification"
                options={brandCerts.map((c) => ({ id: c.id, label: c.name }))}
                value={certIds}
                onChange={setCertIds}
              />
            </FilterBlock>

            <FilterBlock label="Editorial flags">
              <div className="flex flex-col gap-1.5" data-testid="brands-flags-group">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={featuredOnly}
                    onChange={(e) => setFeaturedOnly(e.target.checked)}
                    className="accent-forest"
                    data-testid="brands-featured-toggle"
                  />
                  <span className={featuredOnly ? "text-ink" : "text-muted_text"}>Featured only</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={excludeWarnings}
                    onChange={(e) => setExcludeWarnings(e.target.checked)}
                    className="accent-forest"
                    data-testid="brands-exclude-warnings-toggle"
                  />
                  <span className={excludeWarnings ? "text-ink" : "text-muted_text"}>Exclude brands with warnings</span>
                </label>
              </div>
            </FilterBlock>
          </aside>

          {/* Results */}
          <div>
            {isLoading ? (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">{Array(8).fill(0).map((_, i) => <div key={i} className="h-64 bg-champagne/30 rounded-2xl animate-pulse" />)}</div>
            ) : visibleBrands.length === 0 ? (
              <div className="text-center py-8 text-muted_text" data-testid="brands-empty">No brands match those filters.</div>
            ) : (
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="brands-grid">
                {visibleBrands.map((b, i) => (
                  <BrandCard key={b.id} brand={b} />
                ))}
              </div>
            )}
            {visibleBrands.length > 0 && <AdSlot slotKey="brands_mid" />}
          </div>
        </div>
      </section>
    </div>
  );
}

function FilterBlock({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted_text mb-2">{label}</div>
      {children}
    </div>
  );
}
